import React, { memo, useEffect, useState } from 'react';

import {
	Confirm,
	DynamicIconWrapper,
	FileTypeSelectionCommonForm,
	IconButton,
	Icons,
	Modal,
	SubmissionButtons } from '@components/common';
import ActionsMenu from '@components/common/ActionsMenu';
import KpiThresholdsModal
	from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/KpiThresholdsModal';
import {
	TOnRename, TOnSave,
} from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/PresetForm/ThresholdPresetForm';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { IEditThresholdDTO } from '@interfaces/alarms/dto/thresholdDTO';
import { IPresetThreshold, IThreshold } from '@interfaces/alarms/threshold';
import {
	IOranKpiThresholdUpdateDTO,
	IPresetThresholdCreateDTO,
	IPresetThresholdDataCreateDTO,
	IPresetThresholdUpdateDTO,
} from '@interfaces/kpi/dto/kpiPresetsDTO';
import { IChangeThreshold, IThresholdsTechnologyProps, UpdateOranThresholdPresetHandler } from './interfaces';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { TVendorsValues } from '@enums/common';
import { ESaveTypeId, EThresholdTypes } from './enums';

import { exportRadioButtonsData } from '@constants/common';
import { thresholdTitles } from '../constants';
import { SaveTypes } from './constants';

import PresetMenu from './Preset';
import ThresholdPresetForm from './PresetForm';
import ThresholdValue from './ThresholdValue';
import { validateThresholdsValues } from './utils';

import styles from './KpiThresholdsTechnology.module.scss';

const CONFIRM_MODAL = {
	QUESTION: 'Are you sure you want to apply new values for the thresholds?',
	CHILDREN: 'The changes will effect KPI charts and KPI Alert module.',
} as const;

enum EFormMode {
	create = 'create',
	rename = 'rename',
	edit = 'edit',
}

const KpiThresholdsTechnology = ({
	createPresetThreshold,
	deletePresetThreshold,
	exportThresholds,
	isDirty,
	isError,
	isLoadingPresets,
	isOranVendor,
	presetValue,
	presetsThreshold,
	renamePresetThreshold,
	setIsDirty,
	setIsError,
	setPresetValue,
	technology,
	thresholds,
	updateOranThresholdPreset,
	updateThresholds,
	vendor,
	exportOranThresholds,
}: IThresholdsTechnologyProps) => {
	const authState = useAppSelector(auth);

	const {
		accessPermission:
			{
				networkOperatorOrganizationAdministrator,
				networkOperatorKpiUpdater,
				networkOperatorThresholdKpiUpdater,
				assetConsumerOrganizationAdministrator,
			},
	} = authState;

	const isManagePresetsAccess = (
		networkOperatorOrganizationAdministrator ||
		networkOperatorThresholdKpiUpdater ||
		assetConsumerOrganizationAdministrator
	);

	const isEditThresholdValuesAccess = (
		networkOperatorOrganizationAdministrator ||
		networkOperatorKpiUpdater ||
		networkOperatorThresholdKpiUpdater ||
		assetConsumerOrganizationAdministrator
	);

	const [oranSaveTypes, setOranSaveTypes] = useState(SaveTypes);
	const [selectedOranSaveType, setSelectedOranSaveType] = useState<ESaveTypeId | null>(null);

	const [editablePreset, setEditablePreset] = useState<TSelectOptionType<string> & IPresetThreshold | null>(null);
	const [formMode, setFormMode] = useState<EFormMode | null>(null);
	const [fullEditablePreset, setFullEditablePreset] = useState<TSelectOptionType<string> & IPresetThreshold | null>();
	const [idForDeletePreset, setIdForDeletePreset] = useState<string>('');
	const [localValuesThresholds, setLocalValuesThresholds] = useState<IThreshold[]>(thresholds);
	const [thresholdsForNewPreset, setThresholdsForNewPreset] = useState<IThreshold[]>([]);

	const [isConfirmModal, setIsConfirmModal] = useState(false);
	const [isDirtyPresetForm, setIsDirtyPresetForm] = useState(false);
	const [isDirtyThresholdsModal, setIsDirtyThresholdsModal] = useState(false);
	const [isEditPresetModalOpen, setIsEditPresetModalOpen] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	const [isOpenDeleteConfirmPreset, setIsOpenDeleteConfirmPreset] = useState(false);
	const [isPresetRenameModal, setIsPresetRenameModal] = useState(false);
	const [isSaveOptionsOpen, setIsSaveOptionsOpen] = useState(false);
	const [isSecondaryActionInModal, setIsSecondaryActionInModal] = useState(false);

	useEffect(() => {
		if (editablePreset && thresholds.length) {
			const fullPreset = {
				...editablePreset,
				thresholds: editablePreset.thresholds.map(threshold => ({
					...threshold,
					kpiName: thresholds.find(item => item.kpiId === threshold.kpiId)?.kpiName,
				})),
			};

			setFullEditablePreset(fullPreset);
		}
	}, [editablePreset, thresholds]);

	useEffect(() => {
		setLocalValuesThresholds(thresholds);
	}, [thresholds]);

	useEffect(() => {
		if (isEditPresetModalOpen && editablePreset) {
			const updatedPreset = presetsThreshold.find(preset => (
				preset?.id === editablePreset.id
			)) as (TSelectOptionType<string> & IPresetThreshold) | undefined;

			if (updatedPreset && thresholds.length) {
				const updatedFullPreset = {
					...updatedPreset,
					thresholds: updatedPreset.thresholds.map(threshold => ({
						...threshold,
						kpiName: thresholds.find(item => item.kpiId === threshold.kpiId)?.kpiName,
					})),
				};

				setEditablePreset(updatedPreset);
				setFullEditablePreset(updatedFullPreset);
			}
		}
	}, [presetsThreshold, isEditPresetModalOpen, editablePreset, thresholds]);
	useEffect(() => {
		if (!thresholds.length) {
			return;
		}

		if (presetValue) {
			setIsDirty(true);

			setLocalValuesThresholds(prevState => {
				return prevState.map(threshold => {
					const currentPreset = presetValue.thresholds.find(item => item.kpiId === threshold.kpiId);
					const minThreshold = currentPreset?.minThreshold ?? null;
					const maxThreshold = currentPreset?.maxThreshold ?? null;

					return ({
						...threshold,
						minThreshold,
						maxThreshold,
					});
				});
			});
		}
	}, [thresholds, presetValue, setIsDirty]);

	useEffect(() => {
		const isPass = validateThresholdsValues(localValuesThresholds);

		setIsError(!isPass);
	}, [localValuesThresholds, setIsError]);

	useEffect(() => {
		setOranSaveTypes(prevState => {
			return prevState.map(oranSaveType => {
				const isSaveEdited = oranSaveType.id === ESaveTypeId.saveEdited;
				const isDisabled = isSaveEdited && !presetValue;

				return ({
					...oranSaveType,
					disabled: isDisabled,
				});
			});
		});
	}, [presetValue]);

	const onReset = () => {
		setLocalValuesThresholds(thresholds);
		setIsDirty(false);
		setPresetValue(null);
	};

	const onChangeThreshold = ({ kpiId, value, type }: IChangeThreshold) => {
		setIsDirty(true);

		if (!isOranVendor) {
			setPresetValue(null);
		}

		setLocalValuesThresholds(prevState => prevState.map(threshold => {
			const isCurrentThreshold = threshold.kpiId === kpiId;
			const minThreshold = (type === EThresholdTypes.min && isCurrentThreshold)
				? value
				: threshold.minThreshold;
			const maxThreshold = (type === EThresholdTypes.max && isCurrentThreshold)
				? value
				: threshold.maxThreshold;

			return ({
					...threshold,
					minThreshold,
					maxThreshold,
				}
			);
		}));
	};

	const openConfirmModal = () => setIsConfirmModal(true);

	const closeConfirmModal = () => setIsConfirmModal(false);

	const onSubmit = () => {
		if (!vendor) {
			return;
		}

		const submitData: IEditThresholdDTO = {
			technologyType: technology,
			vendor,
			thresholds: localValuesThresholds.map(threshold => {
				return ({
					kpiId: threshold.kpiId,
					minThreshold: threshold.minThreshold,
					maxThreshold: threshold.maxThreshold,
				});
			}),
		};

		updateThresholds(submitData);

		closeConfirmModal();
		setIsDirty(false);

		if (presetValue) {
			setPresetValue(null);
		}
	};

	const closePresetForm = () => {
		setIsDirtyPresetForm(false);
		setIsPresetRenameModal(false);

		if (editablePreset) {
			setEditablePreset(null);
		}
	};

	const openPresetForm = () => {
		if (isOranVendor) {
			setIsSaveOptionsOpen(prevState => !prevState);
		} else {
			setIsPresetRenameModal(true);
		}
	};

	const closeSaveTypesMenu = () => setIsSaveOptionsOpen(false);

	const closeEditPresetModal = () => {
		setIsEditPresetModalOpen(false);
		setIsDirtyThresholdsModal(false);
		setEditablePreset(null);
	};

	const handleOranPresetSave = (type: ESaveTypeId) => {
		setSelectedOranSaveType(type);
		setIsSaveOptionsOpen(false);
		setIsPresetRenameModal(true);
	};

	const createPreset = ({
		name,
		setNameError,
		description,
	}: Omit<IPresetThresholdCreateDTO, 'data'>) => {
		if (!vendor) {
			return;
		}

		const thresholds = isSecondaryActionInModal ? thresholdsForNewPreset : localValuesThresholds;

		const submitData: IPresetThresholdDataCreateDTO = {
			name,
			technology: technology,
			vendor,
			description,
			thresholds: thresholds.map(threshold => {
				return ({
					kpiId: threshold.kpiId,
					minThreshold: threshold.minThreshold,
					maxThreshold: threshold.maxThreshold,
				});
			}),
		};

		createPresetThreshold({
			data: submitData,
			closePresetForm: () => {
				closeEditPresetModal();
				closePresetForm();
				setIsSecondaryActionInModal(false);
				setThresholdsForNewPreset([]);
				setIsEditPresetModalOpen(false);
			},
			setNameError,
		});
	};

	const renamePreset = ({
		name,
		setNameError,
		description,
	}: IPresetThresholdUpdateDTO) => {
		if (!vendor || !editablePreset?.id) {
			return;
		}

		renamePresetThreshold({
			name,
			id: editablePreset.id,
			closePresetForm,
			setNameError,
			description,
		});
	};

	const editOranThresholdPreset = ({
		id,
		data,
		setNameError,
		closePresetForm,
	}: IOranKpiThresholdUpdateDTO) => {
		if (!vendor) {
			return;
		}

		updateOranThresholdPreset({
			id,
			data,
			closePresetForm,
			setNameError,
		});
	};

	const closeDeleteConfirmPreset = () => setIsOpenDeleteConfirmPreset(false);

	const handleDeletePreset = () => {
		deletePresetThreshold({ id: idForDeletePreset });
		setIsOpenDeleteConfirmPreset(false);
	};

	const exportHandler = () => {
		if (!vendor) {
			return;
		}

		if (isOranVendor) {
			setIsExportModalOpen(true);
		} else {
			exportThresholds({ vendor, technology });
		}
	};

	const closeExportModal = () => setIsExportModalOpen(false);

	const handleRanExport = ({ fileType }: { fileType: string }) => {
		exportOranThresholds({ fileType, vendor: vendor as TVendorsValues });
	};

	const handleSaveChangesToCurrentPresetOran = (thresholdValues: IThreshold[]) => {
		if (!vendor || !editablePreset?.id) {
			return;
		}

		updateOranThresholdPreset({
			id: editablePreset.id,
			data: {
				thresholds: thresholdValues,
			},
			closePresetForm: closeEditPresetModal,
		});
	};

	const handleSecondaryAction = (thresholdValues: IThreshold[]) => {
		setThresholdsForNewPreset(thresholdValues);
		setSelectedOranSaveType(ESaveTypeId.saveNew);
		setIsSaveOptionsOpen(false);
		setIsSecondaryActionInModal(true);
		setFormMode(EFormMode.create);
		setIsPresetRenameModal(true);
	};

	const isUpdateOran = isOranVendor && selectedOranSaveType === ESaveTypeId.saveEdited;

	const presetFormTitle = formMode === EFormMode.create
		? 'Save as preset'
		: editablePreset && !isUpdateOran && !isSecondaryActionInModal
			? 'Rename preset'
			: isUpdateOran ? 'Save changes to current preset' : 'Save as preset';

	const handlePresetFormClose = () => {
		closePresetForm();
		setFormMode(null);
		setEditablePreset(null);
		setSelectedOranSaveType(null);
		setIsSecondaryActionInModal(false);
		setIsDirtyPresetForm(false);
		setIsPresetRenameModal(false);
	};

	return (
		<div className={styles.thresholdsTechnologyContainer}>
			<Modal
				isOpen={isExportModalOpen}
				onClose={closeExportModal}
				title='File extension to export'
			>
				<FileTypeSelectionCommonForm
					closeModals={closeExportModal}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={handleRanExport}
					toggleModal={closeExportModal}
				/>
			</Modal>
			<Modal
				isOpen={isConfirmModal}
				onClose={closeConfirmModal}
			>
				<Confirm
					onClose={closeConfirmModal}
					onSubmit={onSubmit}
					question={CONFIRM_MODAL.QUESTION}
				>
					{CONFIRM_MODAL.CHILDREN}
				</Confirm>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyThresholdsModal}
				isOpen={!!(isEditPresetModalOpen && fullEditablePreset)}
				isScrollableContent
				maxWidth='1100px'
				minWidth='80px'
				onClose={closeEditPresetModal}
				title='Edit preset'
				width='80%'
			>
				<KpiThresholdsModal
					closeModal={closeEditPresetModal}
					fullPresetData={fullEditablePreset}
					isDirtyForm={isDirtyThresholdsModal}
					isReadOnly={false}
					isSecondaryAction={true}
					secondaryActionButtonLabel='Save as new preset'
					secondaryActionHandler={handleSecondaryAction}
					setIsConfirmModal={setIsConfirmModal}
					setIsDirtyForm={setIsDirtyThresholdsModal}
					submitButtonLabel='Save'
					submitCustomHandler={handleSaveChangesToCurrentPresetOran}
					thresholds={(fullEditablePreset?.thresholds ?? []) as IThreshold[]}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyPresetForm}
				isOpen={isPresetRenameModal}
				onClose={handlePresetFormClose}
				title={presetFormTitle}
			>
				<ThresholdPresetForm
					editOranThresholdPreset={editOranThresholdPreset as UpdateOranThresholdPresetHandler}
					editablePreset={formMode === EFormMode.create ? null : editablePreset}
					handleClose={handlePresetFormClose}
					isLoading={isLoadingPresets}
					localValuesThresholds={localValuesThresholds}
					onRename={renamePreset as TOnRename}
					onSave={createPreset as TOnSave}
					presetValue={presetValue}
					selectedSaveType={selectedOranSaveType as ESaveTypeId}
					setIsDirty={setIsDirtyPresetForm}
				/>
			</Modal>
			<Modal
				isOpen={isOpenDeleteConfirmPreset}
				onClose={closeDeleteConfirmPreset}
			>
				<Confirm
					onClose={closeDeleteConfirmPreset}
					onSubmit={handleDeletePreset}
					question='Are you sure you want to delete preset?'
				/>
			</Modal>
			<div className={styles.controlPanel}>
				<DynamicIconWrapper
					icon={Icons.export}
					isDisabled={!localValuesThresholds.length}
					onClick={exportHandler}
					tooltipLabel='Export thresholds'
				/>
				{isManagePresetsAccess && (
					<PresetMenu
						isLoadingPresets={isLoadingPresets}
						presetValue={presetValue}
						presetsThreshold={presetsThreshold}
						setEditablePreset={setEditablePreset}
						setIdForDeletePreset={setIdForDeletePreset}
						setIsEditPresetModalOpen={setIsEditPresetModalOpen}
						setIsOpenDeleteConfirmPreset={setIsOpenDeleteConfirmPreset}
						setIsPresetModal={setIsPresetRenameModal}
						setPresetValue={setPresetValue}
					/>
				)}
			</div>
			<div className={styles.thresholds}>
				{!vendor
					? <div className={styles.emptyVendor}>Select vendor</div>
					: !!localValuesThresholds.length && (
					<>
						<div className={styles.title}>
							{thresholdTitles.map(title => <div key={title}>{title}</div>)}
						</div>
						{
							localValuesThresholds.map(threshold => {
								return (
									<ThresholdValue
										key={threshold.kpiId}
										isDisabled={!isEditThresholdValuesAccess}
										onChange={onChangeThreshold}
										threshold={threshold}
									/>
								);
							})
						}
					</>
				)}
			</div>
			{(isManagePresetsAccess || isEditThresholdValuesAccess) && (
				<div className={styles.buttons}>
					<div className={styles.presetButton}>
						{isManagePresetsAccess
							&& <IconButton
								icon={Icons.saveIcon}
								isDisabled={isError || !localValuesThresholds.length}
								onClick={openPresetForm}
								tooltipLabel='Save as preset'
							/>
						}
						<ActionsMenu
							actions={oranSaveTypes}
							closeMenu={closeSaveTypesMenu}
							containerClassName={styles.saveOptionsMenu}
							isVisibleMenu={isSaveOptionsOpen}
							onClick={handleOranPresetSave}
							position='top'
						/>
					</div>
					{isEditThresholdValuesAccess
						&& <SubmissionButtons
							disabledNegative={!isDirty}
							disabledPositive={!isDirty || isError}
							negativeHandler={onReset}
							negativeLabel='Reset changes'
							positiveHandler={openConfirmModal}
							positiveLabel='Apply'
						/>
					}
				</div>
			)}
		</div>
	);
};

export default memo(KpiThresholdsTechnology);
